/**
 * Owner: Masa Bayu
 * File: resolve-absence.ts
 * Date:Time: 2026-10-07 12:24
 * Description: QX-URP-02 STANDARD absence defaults · source always wins.
 */

import type { WitnessedBoolean, WitnessedNumber } from './types';

export type AbsenceResolution<T> = {
  readonly value: T;
  readonly provenance: 'source' | 'absence-default';
};

function isPresentNumber(v: WitnessedNumber): v is number {
  return typeof v === 'number' && Number.isFinite(v);
}

function isPresentBoolean(v: WitnessedBoolean): v is boolean {
  return typeof v === 'boolean';
}

/** STANDARD: rotation absent → 0 · witnessed → source. */
export function resolveRotation(
  witnessed: WitnessedNumber,
): AbsenceResolution<number> {
  if (isPresentNumber(witnessed)) {
    return { value: witnessed, provenance: 'source' };
  }
  return { value: 0, provenance: 'absence-default' };
}

/** STANDARD: opacity absent → 1 · witnessed → source. */
export function resolveOpacity(
  witnessed: WitnessedNumber,
): AbsenceResolution<number> {
  if (isPresentNumber(witnessed)) {
    return { value: witnessed, provenance: 'source' };
  }
  return { value: 1, provenance: 'absence-default' };
}

/** STANDARD: visibility absent → true · witnessed → source. */
export function resolveVisibility(
  witnessed: WitnessedBoolean,
): AbsenceResolution<boolean> {
  if (isPresentBoolean(witnessed)) {
    return { value: witnessed, provenance: 'source' };
  }
  return { value: true, provenance: 'absence-default' };
}

/** SOURCE_DERIVED pass-through · never invent. */
export function resolveSourceNumber(
  witnessed: WitnessedNumber,
): number | null {
  return isPresentNumber(witnessed) ? witnessed : null;
}

/** SOURCE_DERIVED / UNKNOWN string pass-through. */
export function resolveSourceString(
  witnessed: string | null | undefined,
): string | null {
  if (typeof witnessed === 'string' && witnessed.length > 0) return witnessed;
  return null;
}
